<template>
  <div class="space-y-24">
    <h2 class="heading-2">{{ i18n(translations.title) }}</h2>

    <p class="body-1">
      {{ i18n(translations.description) }}
    </p>

    <RevTextList variant="ordered">
      <RevTextListItem>
        <FormattedMessage :definition="translations.step1">
          <template #templateLink>
            <RevLink href="#" target="_blank">{{
              i18n(translations.templateLink)
            }}</RevLink>
          </template>
        </FormattedMessage>
      </RevTextListItem>

      <RevTextListItem>
        {{ i18n(translations.step2) }}
      </RevTextListItem>

      <RevTextListItem>
        {{ i18n(translations.step3) }}
      </RevTextListItem>

      <RevTextListItem>
        {{ i18n(translations.step4) }}
      </RevTextListItem>

      <RevTextListItem>
        {{ i18n(translations.step5) }}
      </RevTextListItem>
    </RevTextList>

    <!-- Display existing letter of authorization if available -->
    <div v-if="existingLetterOfAuthorization" class="space-y-16">
      <h3 class="heading-3">{{ i18n(translations.currentDocument) }}</h3>
      <div
        class="border-static-default-mid rounded-lg flex items-center border p-16"
      >
        <div class="flex items-center space-x-12">
          <div
            class="rounded bg-surface-default-mid flex h-40 w-40 items-center justify-center"
          >
            <span class="text-static-default-hi body-2-bold">PDF</span>
          </div>
          <span class="body-1">{{ existingLetterOfAuthorization }}</span>
        </div>
      </div>
    </div>

    <RevForm
      class="space-y-24"
      :initial-values
      :validate
      @submit="handleSubmit"
    >
      <template #default="{ values, errors, hasError }">
        <RevInputFileUpload
          id="letter-of-authorization-file-input"
          v-model="values.letterOfAuthorization"
          :accept="['.pdf', '.jpg', '.jpeg', '.png']"
          :description="
            errors.letterOfAuthorization ??
            i18n(translations.fileUploadDescription)
          "
          :errors="errors.letterOfAuthorization as any"
          :label-empty="i18n(translations.fileUploadLabel)"
          :max-file-size="5 * 1024 * 1024"
          :max-files="1"
        />

        <div class="flex justify-end">
          <RevButton
            :disabled="hasError || isSubmitting"
            :loading="isSubmitting"
            type="submit"
            variant="primary"
          >
            {{ i18n(translations.saveButton) }}
          </RevButton>
        </div>
      </template>
    </RevForm>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInputFileUpload } from '@ds/components/InputFileUpload'
import { RevLink } from '@ds/components/Link'
import { RevTextList } from '@ds/components/TextList'
import { RevTextListItem } from '@ds/components/TextListItem'

import type { KybData } from '../../../Index.types'

import translations from './LetterOfAuthorization.translations'

type FormValues = {
  letterOfAuthorization: File[]
}

const props = defineProps<{
  kybData: KybData
}>()

const i18n = useI18n()
const isSubmitting = ref(false)

// Computed to get existing letter of authorization data
const existingLetterOfAuthorization = computed(
  () => props.kybData?.letterOfAuthorization,
)

const initialValues: FormValues = {
  letterOfAuthorization: [],
}

const validate = computed(() => {
  // If there's already a letter of authorization, the field is optional
  const isRequired = !existingLetterOfAuthorization.value

  return makeValidate<FormValues>({
    letterOfAuthorization: isRequired
      ? [required(`${i18n(translations.fileUploadLabel)} is required`)]
      : [],
  })
})

async function handleSubmit(formValues: FormValues) {
  try {
    isSubmitting.value = true

    // TODO: Implement API call to save letter of authorization
    // eslint-disable-next-line no-console
    console.log('Submitting letter of authorization:', formValues)

    // Simulate API call
    await new Promise<void>((resolve) => {
      setTimeout(() => resolve(), 1000)
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error submitting letter of authorization:', error)
  } finally {
    isSubmitting.value = false
  }
}
</script>
